import { datadogRum, DefaultPrivacyLevel, type RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { createBrowserRouter as createBrowserRouterRUM } from '@datadog/browser-rum-react/react-router-v6';
import type { MetricWriter } from 'experimentation-client-js';
import { createBrowserRouter as createBrowserRouterReact } from 'react-router-dom';

import type { IDatadog } from './types';

declare global {
    interface Window {
        OneTrust?: {
            acceptedCookieList: string[];
            isAllowedAll: boolean;
            isBannerClosed: boolean;
            IsAlertBoxClosed(): boolean;
            OnConsentChanged?: (handler: OnConsentChangedHandler) => void;
        };
        OnetrustActiveGroups?: string;
        OptanonWrapper?: () => void;
    }
}
type OnConsentChangedHandler = (event: { detail: string[] }) => void;

class DatadogRUM {
    private sessionSampleRate: number;
    private replaySampleRate: number;
    private replayDefaultMasking: DefaultPrivacyLevel;
    private supportReactRouter: boolean;
    private oneTrustAnalyticsCategoryId: string | undefined;

    private isInited: boolean;

    private static readonly REDACTED_VIEW_PROPS = ['url', 'referrer'] as const;
    private static readonly SENSITIVE_QUERY_PARAMS = [
        'token',
        'uuid',
        'track-id',
        'auth_token',
        'token_wellfunnel',
    ] as const;
    private static readonly SENSITIVE_ACTION_NAME_REGEX = [
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, // email addresses
    ];

    constructor() {
        this.sessionSampleRate = 20;
        this.replaySampleRate = 0;
        this.replayDefaultMasking = DefaultPrivacyLevel.MASK;
        this.supportReactRouter = true;
        this.oneTrustAnalyticsCategoryId = undefined;

        this.isInited = false;
    }

    init(config: IDatadog) {
        if (this.isInited) return;

        this.sessionSampleRate = config.sessionSampleRate ?? this.sessionSampleRate;
        this.replaySampleRate = config.replaySampleRate ?? this.replaySampleRate;
        this.replayDefaultMasking = config.replayDefaultMasking ?? this.replayDefaultMasking;
        this.supportReactRouter = config.supportReactRouter ?? this.supportReactRouter;
        this.oneTrustAnalyticsCategoryId = config.oneTrustAnalyticsCategoryId ?? '';

        datadogRum.init({
            applicationId: config.applicationId,
            clientToken: config.clientToken,
            site: 'datadoghq.com', // DO NOT CHANGE
            service: config.serviceName,
            env: config.env,
            version: config.version,
            sessionSampleRate: this.sessionSampleRate,
            sessionReplaySampleRate: this.replaySampleRate,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: this.replayDefaultMasking,
            trackingConsent: config.trackingConsent ?? 'granted',
            plugins: this.supportReactRouter ? [reactPlugin({ router: true })] : ([] as any),
            beforeSend: (event, _context) => this.beforeSend(event),
        });

        datadogRum.setViewContextProperty('time_origin', Math.ceil(performance.timeOrigin));

        if (this.replaySampleRate > 0) {
            datadogRum.startSessionReplayRecording();
        }

        if (this.oneTrustAnalyticsCategoryId) {
            this.connectOneTrust();
        }

        this.isInited = true;
    }

    setUser(id: number | string, email?: string | null) {
        datadogRum.setUser({ id: id.toString(), ...(email && { email }) });
    }

    getUser() {
        return datadogRum.getUser();
    }

    clearUser() {
        datadogRum.clearUser();
    }

    addAction(name: string, context?: Record<string, unknown>) {
        datadogRum.addAction(name, context);
    }

    addError(error: unknown, context?: Record<string, unknown>) {
        datadogRum.addError(error instanceof Error ? error : String(error), context);
    }

    private hasOneTrustConsent() {
        return Boolean(
            this.oneTrustAnalyticsCategoryId &&
                window.OnetrustActiveGroups?.split(',').includes(this.oneTrustAnalyticsCategoryId)
        );
    }

    private applyOneTrustConsent() {
        datadogRum.setTrackingConsent(this.hasOneTrustConsent() ? 'granted' : 'not-granted');
    }

    private connectOneTrust() {
        const attachOnConsentChanged = () => {
            if (window.OneTrust?.OnConsentChanged) {
                window.OneTrust.OnConsentChanged(() => this.applyOneTrustConsent());
            }
        };

        if (window.OnetrustActiveGroups) {
            this.applyOneTrustConsent();
            attachOnConsentChanged();
        }

        const prevWrapper = window.OptanonWrapper;

        window.OptanonWrapper = () => {
            this.applyOneTrustConsent();
            attachOnConsentChanged();
            prevWrapper?.();
        };
    }

    private beforeSend(event: RumEvent): boolean {
        const view = event.view as { url: string; referrer?: string };
        for (const prop of DatadogRUM.REDACTED_VIEW_PROPS) {
            if (!view[prop]) continue;
            const propValue = view[prop] ?? '';
            view[prop] = DatadogRUM.SENSITIVE_QUERY_PARAMS.reduce(
                (str, param) => str.replace(new RegExp(`${param}=[^&]*`, 'g'), `${param}=REDACTED`),
                propValue
            );
        }
        const action = (event as any).action as { target?: { name: string } } | undefined;
        if (action?.target?.name) {
            action.target.name = DatadogRUM.SENSITIVE_ACTION_NAME_REGEX.reduce(
                (str, regex) => str.replace(regex, 'REDACTED'),
                action.target.name
            );
        }

        if (event.context) {
            event.context.spa_time_open = Math.ceil(new Date().getTime() - performance.timeOrigin);
        }

        return true;
    }

    getReactRouter() {
        if (!this.supportReactRouter) {
            return createBrowserRouterReact;
        } else {
            return createBrowserRouterRUM;
        }
    }

    getExperimentationMetricWriter(): MetricWriter {
        return {
            namespace: 'experimentation',
            sendGaugeMetric: async (metricName, value, dimensions) => {
                datadogRum.addAction(metricName, { value, ...dimensions });
            },
            sendCountMetric: async (metricName, value, dimensions) => {
                datadogRum.addAction(metricName, { value, ...dimensions });
            },
        };
    }
}

export { datadogRum };

export default new DatadogRUM();
